import { Children, FC, ReactNode, useEffect, useState } from "react";
import styles from "./Header.module.scss";
import classNamesBind from "classnames/bind";
import { NavLink, useLocation } from "react-router";
import { useHeaderLoading } from "./HeaderLoading";
import { Icon } from "./Icon";

const cn = classNamesBind.bind(styles);

export interface Props {
  title: ReactNode;
  titleMenuButton?: ReactNode | null | undefined;
  subTitle?: ReactNode | null | undefined;

  /**
   * Indicate that we're displaying cached content or loading and should
   * indicate this to the user.
   */
  isLoading?: boolean | null | undefined;
  bigContainer?: boolean | null | undefined;

  back?: ReactNode | null | undefined;
  children?: ReactNode;
}

export interface TabProps {
  to: string;
  end?: boolean;
  icon?: ReactNode;
  isActive?: (location: { pathname: string; search: string }) => boolean;
  children?: ReactNode;
}

export const Tab: FC<TabProps> = function Tab({
  to,
  end,
  children,
  icon,
  isActive,
}) {
  const location = useLocation();
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive: active }) =>
        cn("tab", { active: isActive ? isActive(location) : active })
      }
    >
      <span className={cn("icon")}>{icon}</span>
      <span className={cn("text")}>{children}</span>
    </NavLink>
  );
};

export const Header: FC<Props> = function Header({
  title,
  titleMenuButton,
  subTitle,
  isLoading: _isLoading,
  bigContainer,
  back,
  children,
}) {
  const [debouncedCached, setDebouncedCached] = useState(false);
  const isLoadingContext = useHeaderLoading();
  const isLoading = _isLoading || isLoadingContext;

  useEffect(() => {
    const _cached = isLoading ?? false;

    if (!_cached) {
      setDebouncedCached(false);
      return;
    }

    const interval = setInterval(() => setDebouncedCached(true), 500);
    return () => clearInterval(interval);
  }, [isLoading]);

  return (
    <div className={cn("wrapper", { cached: debouncedCached })}>
      <header
        className={cn(bigContainer ? "big-header" : "header", {
          "no-nav": Children.count(children) === 0,
        })}
      >
        <div className={cn("back")}>
          {back ? (
            <div>
              <Icon
                icon="chevron-left"
                type="regular"
                className={cn("back-chevron")}
              />{" "}
              {back}
            </div>
          ) : null}
        </div>
        <div className={cn("primary")}>
          <h1 className={cn("title")}>
            {title}
            <span className={cn("menu-button")}>{titleMenuButton}</span>
          </h1>

          {Children.count(children) > 0 && (
            <nav className={cn("nav")}>{children}</nav>
          )}
        </div>

        <div className={cn("secondary")}>
          {subTitle && <h2 className={cn("sub-title")}>{subTitle}</h2>}
        </div>
      </header>
    </div>
  );
};
